import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
    Check,
    X,
    User,
} from "lucide-react";

// ---------- Orders Tab (user-submitted, admin-confirmed) ----------
export default function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [confirmDialog, setConfirmDialog] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = filter === "all" ? {} : { status: filter };
            const { data } = await api.get("/admin/orders", { params });
            setOrders(data);
        } catch {}
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const doConfirm = async (o) => {
        try {
            await api.post(`/admin/orders/${o.id}/confirm`);
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Failed to confirm");
        }
    };

    const askConfirm = (o) => setConfirmDialog({
        title: "Konfirmo porosinë?",
        message: `Kjo do të zbresë stokun për ${o.items.length} produkte. Vazhdoj?`,
        onConfirm: () => { doConfirm(o); setConfirmDialog(null); },
    });

    const askReject = (o) => setConfirmDialog({
        title: "Refuzo porosinë?",
        message: `Porosia e ${o.user_email} do të shënohet si e refuzuar. Stoku nuk preket.`,
        onConfirm: async () => {
            await api.post(`/admin/orders/${o.id}/reject`);
            setConfirmDialog(null);
            load();
        },
    });

    const statusColor = {
        pending: "bg-amber-100 text-amber-800",
        confirmed: "bg-emerald-100 text-emerald-800",
        rejected: "bg-red-100 text-red-800",
    };

    return (
        <div data-testid="admin-orders-tab" className="space-y-4">
            <div className="flex gap-2">
                {["pending", "confirmed", "rejected", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 font-display font-bold uppercase text-xs tracking-wider border ${filter === s ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600"}`}
                        data-testid={`orders-filter-${s}`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-left">
                            <th className="px-3 py-2 label-kicker">Date</th>
                            <th className="px-3 py-2 label-kicker">User</th>
                            <th className="px-3 py-2 label-kicker">Service</th>
                            <th className="px-3 py-2 label-kicker">Items</th>
                            <th className="px-3 py-2 label-kicker">Status</th>
                            <th className="px-3 py-2 label-kicker text-right">Total €</th>
                            <th className="w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 && (
                            <tr><td colSpan="7" className="px-3 py-8 text-center font-mono text-slate-500">Asnjë porosi.</td></tr>
                        )}
                        {orders.map((o) => (
                            <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`order-row-${o.id}`}>
                                <td className="px-3 py-2 font-mono text-xs text-slate-600">{new Date(o.created_at).toLocaleString()}</td>
                                <td className="px-3 py-2">
                                    <div className="font-bold text-xs">{o.user_email}</div>
                                    <div className="font-mono text-[11px] text-slate-500">{o.user_phone || "—"}</div>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-700">{o.user_service_name || "—"}</td>
                                <td className="px-3 py-2 font-mono text-xs max-w-[280px] truncate">
                                    {o.items.map((it) => `${it.part_number || it.name} × ${it.qty}`).join(", ")}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${statusColor[o.status] || "bg-slate-100"}`}>
                                        {o.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-display font-black text-right">€{(o.total || 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right">
                                    {o.status === "pending" && (
                                        <>
                                            <button onClick={() => askConfirm(o)} className="p-1.5 text-emerald-600 hover:text-emerald-800" title="Confirm" data-testid={`confirm-order-${o.id}`}>
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => askReject(o)} className="p-1.5 text-red-600 hover:text-red-800" title="Reject" data-testid={`reject-order-${o.id}`}>
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
        </div>
    );
}

